"use client";

import { zodResolver } from "@hookform/resolvers/zod";
import { Eye, EyeOff } from "lucide-react";
import { useRouter, useSearchParams } from "next/navigation";
import { useLocale, useTranslations } from "next-intl";
import React from "react";
import { useForm } from "react-hook-form";
import { toast } from "sonner";
import * as z from "zod";
import { authClient } from "@/lib/client/auth/client";
import {
  Box,
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  cn,
  Form,
  FormControl,
  FormField,
  FormItem,
  FormMessage,
  HStack,
  Input,
  VStack,
} from "@/ui";

// define form schema
const formSchema = z
  .object({
    password: z.string().min(1),
    confirm: z.string().min(1),
  })
  .superRefine(({ password, confirm }, ctx) => {
    if (confirm && password !== confirm) {
      // https://stackoverflow.com/questions/76886215/how-use-react-password-checklist-with-zod
      ctx.addIssue({
        code: "custom",
        message: "not_match",
        path: ["confirm"],
      });
    }
  });

export const ResetPasswordPage = () => {
  const locale = useLocale();
  const toastT = useTranslations("toast");
  const generalT = useTranslations("general");
  const validationT = useTranslations("validation");
  const resetPasswordT = useTranslations("reset_password");
  const errorsT = useTranslations("errors");
  const [showPassword, setShowPassword] = React.useState(false);
  const [isLoading, setIsLoading] = React.useState(false);
  const router = useRouter();
  const searchParams = useSearchParams();
  const token = searchParams.get("token");
  const tokenError = searchParams.get("error");
  // 1. Define your form.
  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      password: "",
      confirm: "",
    },
  });
  const {
    control,
    handleSubmit,
    formState: { errors },
  } = form;

  const onSubmit = async (values: z.infer<typeof formSchema>) => {
    setIsLoading(true);

    try {
      if (!token) {
        toast.error(errorsT("reset_token_invalid"));
        return;
      }

      const result = await authClient.resetPassword({
        newPassword: values.password,
        token,
      });

      if (result.error) {
        toast.error(result.error.message);
      } else {
        setTimeout(() => router.push(`/${locale}`), 1500);
        toast.success(toastT("success.title"), {
          description: resetPasswordT("success_message"),
          duration: 3000,
        });
      }
    } catch (error) {
      toast.error(error instanceof Error ? error.message : errorsT("failed_to_reset_password"));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Card className="mx-auto mt-8 w-[350px]">
      <CardHeader>
        <CardTitle>{resetPasswordT("title")}</CardTitle>
        <CardDescription>
          {tokenError === "INVALID_TOKEN"
            ? resetPasswordT("description_invalid")
            : resetPasswordT("description_valid")}
        </CardDescription>
      </CardHeader>
      <CardContent>
        <Form {...form}>
          <form onSubmit={handleSubmit(onSubmit)}>
            <VStack className="w-full items-center space-y-2">
              <FormField
                control={control}
                name="password"
                render={({ field }) => (
                  <FormItem>
                    <FormControl>
                      <HStack className={"items-center justify-end"}>
                        <Input
                          placeholder={generalT("password")}
                          {...field}
                          type={showPassword ? "text" : "password"}
                        />
                        <Box
                          onClick={() => setShowPassword(!showPassword)}
                          className={"absolute mr-2 cursor-pointer"}
                        >
                          {showPassword ? (
                            <Eye className={"h-5 w-5"} />
                          ) : (
                            <EyeOff className={"h-5 w-5"} />
                          )}
                        </Box>
                      </HStack>
                    </FormControl>
                    <FormMessage>
                      {errors.password && validationT("required", { field: "password" })}
                    </FormMessage>
                  </FormItem>
                )}
              />
              <FormField
                control={control}
                name="confirm"
                render={({ field }) => (
                  <FormItem>
                    <FormControl>
                      <HStack className={"items-center justify-end"}>
                        <Input
                          placeholder={generalT("confirm_password")}
                          {...field}
                          type={showPassword ? "text" : "password"}
                        />
                        <Box
                          onClick={() => setShowPassword(!showPassword)}
                          className={"absolute mr-2 cursor-pointer"}
                        >
                          {showPassword ? (
                            <Eye className={"h-5 w-5"} />
                          ) : (
                            <EyeOff className={"h-5 w-5"} />
                          )}
                        </Box>
                      </HStack>
                    </FormControl>
                    <FormMessage>
                      {errors.confirm?.message === "not_match"
                        ? errorsT("passwords_not_match")
                        : errors.confirm && validationT("required", { field: "confirmPassword" })}
                    </FormMessage>
                  </FormItem>
                )}
              />
              <Button
                type="submit"
                disabled={isLoading || !form.formState.isValid || !token}
                className={"w-full"}
              >
                <span className={cn(isLoading ? "loading loading-spinner" : "hidden")} />
                {resetPasswordT("submit")}
              </Button>
            </VStack>
          </form>
        </Form>
      </CardContent>
    </Card>
  );
};
